/**
 * Shared model + mapping helpers for the order "Advanced fields". Both the Create (`OrderFormDialog`) and Edit
 * (`OrderEditDialog`) forms go through this module so the two never drift.
 *
 * Column mapping:
 *   - `public.legs`   → shuttle carriers (origin/destination), `is_loaded`,
 *                       `is_temperature_controlled`, `temperature_f`
 *   - `public.orders` → shuttle carriers (order-level mirror for single-leg),
 *                       `freight_weight_lbs`, `commodity_code`,
 *                       `commodity_description`
 */
import type { LegData, LoadStatusValue, OrderFormData } from "../orderFormTypes";

export const LOAD_STATUS_OPTIONS: { value: Exclude<LoadStatusValue, "">; label: string }[] = [
  { value: "empty", label: "Empty" },
  { value: "loaded", label: "Loaded" },
];

/** Form value → `legs.is_loaded`. `""` maps to `null` (explicitly unset). */
export function loadStatusToBool(v: LoadStatusValue | undefined): boolean | null {
  if (v === "loaded") return true;
  if (v === "empty") return false;
  return null;
}

/** `legs.is_loaded` → form value. */
export function boolToLoadStatus(v: boolean | null | undefined): LoadStatusValue {
  if (v === true) return "loaded";
  if (v === false) return "empty";
  return "";
}

/** Trim to `null` so empty inputs clear the column instead of storing "". */
function text(v: string | undefined | null): string | null {
  const t = (v ?? "").trim();
  return t === "" ? null : t;
}

/** Parse a numeric text input; blank / invalid → null. */
export function numericOrNull(v: string | undefined | null): number | null {
  const t = (v ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export interface AdvancedLegSource {
  origin_shuttle_carrier_name?: string;
  origin_shuttle_carrier_dot?: string;
  destination_shuttle_carrier_name?: string;
  destination_shuttle_carrier_dot?: string;
  load_status?: LoadStatusValue;
  is_temperature_controlled?: boolean;
  temperature_f?: string;
}

/**
 * Columns to write onto a `public.legs` row (or a legacy `orders` child for
 * the shuttle subset — callers strip leg-only keys where needed).
 */
export function legAdvancedPayload(src: AdvancedLegSource): Record<string, unknown> {
  const tempControlled = src.is_temperature_controlled === true;
  return {
    origin_shuttle_carrier_name: text(src.origin_shuttle_carrier_name),
    origin_shuttle_carrier_dot: text(src.origin_shuttle_carrier_dot),
    destination_shuttle_carrier_name: text(src.destination_shuttle_carrier_name),
    destination_shuttle_carrier_dot: text(src.destination_shuttle_carrier_dot),
    is_loaded: loadStatusToBool(src.load_status),
    is_temperature_controlled: tempControlled,
    temperature_f: tempControlled ? numericOrNull(src.temperature_f) : null,
  };
}

/**
 * Columns to write onto the `public.orders` row. Shuttle carriers are mirrored
 * from the order-level (single-leg) fields — multi-leg callers pass the first /
 * last leg's values so the stitched parent header stays coherent.
 */
export function orderAdvancedPayload(
  data: Pick<
    OrderFormData,
    | "origin_shuttle_carrier_name"
    | "origin_shuttle_carrier_dot"
    | "destination_shuttle_carrier_name"
    | "destination_shuttle_carrier_dot"
    | "freight_weight_lbs"
    | "commodity_code"
    | "commodity_description"
  >,
): Record<string, unknown> {
  return {
    origin_shuttle_carrier_name: text(data.origin_shuttle_carrier_name),
    origin_shuttle_carrier_dot: text(data.origin_shuttle_carrier_dot),
    destination_shuttle_carrier_name: text(data.destination_shuttle_carrier_name),
    destination_shuttle_carrier_dot: text(data.destination_shuttle_carrier_dot),
    freight_weight_lbs: numericOrNull(data.freight_weight_lbs),
    commodity_code: text(data.commodity_code),
    commodity_description: text(data.commodity_description),
  };
}

/** Hydrate the advanced leg fields from a `legs` / legacy child row. */
export function hydrateLegAdvanced(row: Record<string, any> | null | undefined): AdvancedLegSource {
  const r = row ?? {};
  return {
    origin_shuttle_carrier_name: r.origin_shuttle_carrier_name ?? "",
    origin_shuttle_carrier_dot: r.origin_shuttle_carrier_dot ?? "",
    destination_shuttle_carrier_name: r.destination_shuttle_carrier_name ?? "",
    destination_shuttle_carrier_dot: r.destination_shuttle_carrier_dot ?? "",
    load_status: boolToLoadStatus(r.is_loaded),
    is_temperature_controlled: r.is_temperature_controlled === true,
    temperature_f:
      r.temperature_f === null || r.temperature_f === undefined ? "" : String(r.temperature_f),
  };
}

/** Hydrate the order-level advanced fields from an `orders` row. */
export function hydrateOrderAdvanced(row: Record<string, any> | null | undefined) {
  const r = row ?? {};
  return {
    origin_shuttle_carrier_name: r.origin_shuttle_carrier_name ?? "",
    origin_shuttle_carrier_dot: r.origin_shuttle_carrier_dot ?? "",
    destination_shuttle_carrier_name: r.destination_shuttle_carrier_name ?? "",
    destination_shuttle_carrier_dot: r.destination_shuttle_carrier_dot ?? "",
    freight_weight_lbs:
      r.freight_weight_lbs === null || r.freight_weight_lbs === undefined
        ? ""
        : String(r.freight_weight_lbs),
    commodity_code: r.commodity_code ?? "",
    commodity_description: r.commodity_description ?? "",
  };
}

/**
 * Advanced-field validation. Only enforced while the advanced section is
 * visible (flag ON *and* the user expanded it) so the default flow is
 * unchanged. Load status and Commodity code are required in that state.
 */
export function hasAnyAdvancedValues(
  data: Partial<OrderFormData> & { legs?: LegData[] },
): boolean {
  const filled = (v: unknown) => typeof v === "string" && v.trim() !== "";
  const legHas = (l: Partial<LegData>) =>
    filled(l.origin_shuttle_carrier_name) ||
    filled(l.origin_shuttle_carrier_dot) ||
    filled(l.destination_shuttle_carrier_name) ||
    filled(l.destination_shuttle_carrier_dot) ||
    filled(l.load_status) ||
    l.is_temperature_controlled === true ||
    filled(l.temperature_f);
  return (
    legHas(data as Partial<LegData>) ||
    filled(data.freight_weight_lbs) ||
    filled(data.commodity_code) ||
    filled(data.commodity_description) ||
    (data.legs ?? []).some(legHas)
  );
}

export function validateAdvancedFields(data: OrderFormData): string[] {
  if (data.show_advanced_fields !== true) return [];
  const errors: string[] = [];
  if (data.order_type === "multi-leg") {
    data.legs.forEach((leg: LegData, i) => {
      if (!leg.load_status) errors.push(`Stop ${i + 1}: Load status is required`);
    });
  } else if (!data.load_status) {
    errors.push("Load status is required");
  }
  if (!(data.commodity_code ?? "").trim()) {
    errors.push("Commodity code is required");
  }
  return errors;
}
